(() => {
  if (customElements.get('lbb-mobile-buy-bar')) return;

  class LbbMobileBuyBar extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.closest('quick-add-modal, modal-dialog')) return;
      this.initialized = true;
      this.price = this.querySelector('[data-lbb-buy-price]');
      this.options = this.querySelector('[data-lbb-buy-options]');
      this.quantity = this.querySelector('[data-lbb-buy-quantity]');
      this.submit = this.querySelector('[data-lbb-buy-submit]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.onSubmit = () => this.submitNative();
      this.onOptions = () => this.showOptions();
      this.submit.addEventListener('click', this.onSubmit);
      this.options.addEventListener('click', this.onOptions);
      this.events = [
        [window, 'scroll'], [window, 'resize'], [document, 'change'],
        [document, 'input'], [document, 'focusin'], [document, 'focusout'],
        [document.body, 'modalClosed'], [document, 'shopify:section:load']
      ];
      if (window.visualViewport) this.events.push([window.visualViewport, 'resize']);
      this.events.forEach(([target, event]) => target.addEventListener(event, this.schedule, { passive: true }));
      this.mobile.addEventListener('change', this.schedule);
      this.productObserver = new MutationObserver(this.schedule);
      this.overlayObserver = new MutationObserver((mutations) => {
        if (mutations.some(({ target }) => !this.contains(target))) this.schedule();
      });
      this.overlayObserver.observe(document.body, {
        subtree: true, attributes: true, attributeFilter: ['class', 'open', 'aria-hidden', 'hidden', 'inert']
      });
      this.sync();
    }

    disconnectedCallback() {
      this.productObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.events?.forEach(([target, event]) => target.removeEventListener(event, this.schedule));
      this.mobile?.removeEventListener('change', this.schedule);
      this.submit?.removeEventListener('click', this.onSubmit);
      this.options?.removeEventListener('click', this.onOptions);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.setVisible(false);
      this.initialized = false;
    }

    resolveProduct() {
      const product = document.getElementById(`MainProduct-${this.dataset.productSection}`);
      if (product !== this.product) {
        this.productObserver.disconnect();
        this.product = product;
        if (product) this.productObserver.observe(product, { subtree: true, childList: true, characterData: true, attributes: true });
      }
      this.form = document.getElementById(`product-form-${this.dataset.productSection}`);
      this.native = document.getElementById(`ProductSubmitButton-${this.dataset.productSection}`);
      return !!(product && this.form && this.native && product.contains(this.form) && this.native.form === this.form);
    }

    hasOverlay() {
      if (/\boverflow-hidden(?:-mobile|-tablet)?\b/.test(document.body.className)) return true;
      return Array.from(document.querySelectorAll(
        'dialog[open], modal-dialog[open], product-modal[open], quick-add-modal[open], cart-drawer.active, ' +
        'cart-notification.active, header-drawer details[open], menu-drawer details[open], ' +
        '[role="dialog"][aria-modal="true"]'
      )).some((element) => {
        if (element.closest('details:not([open]), [hidden], [inert], [aria-hidden="true"]')) return false;
        return element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
      });
    }

    hasKeyboard() {
      const focused = document.activeElement;
      if (focused?.matches('select, textarea, [contenteditable="true"], input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"])')) return true;
      return !!(window.visualViewport && window.innerHeight - window.visualViewport.height > 160);
    }

    sync() {
      if (!this.resolveProduct() || !this.mobile.matches || document.getElementById('bundlesuiteContainer') || document.getElementById('lbb-kit-formula-ui-config')) {
        this.setVisible(false);
        return;
      }
      const busy = this.native.classList.contains('loading');
      this.submit.disabled = this.native.disabled || this.native.getAttribute('aria-disabled') === 'true' || busy;
      this.submit.setAttribute('aria-busy', String(busy));
      const nativeLabel = Array.from(this.native.querySelectorAll('span')).find((span) => !span.classList.contains('hidden'))?.textContent.trim();
      const label = busy ? this.dataset.loadingLabel : nativeLabel || this.native.textContent.trim();
      if (this.submit.textContent !== label) this.submit.textContent = label;

      const priceRoot = document.getElementById(`price-${this.dataset.productSection}`);
      const selector = priceRoot?.querySelector('.price--on-sale') ? '.price-item--sale' : '.price__regular .price-item--regular';
      const price = priceRoot && !priceRoot.classList.contains('hidden') ? priceRoot.querySelector(selector)?.textContent.trim() || '' : '';
      if (this.price.textContent !== price) this.price.textContent = price;
      const variant = this.selectedVariant();
      const options = variant?.title && this.dataset.defaultVariant !== 'true' ? variant.title : this.dataset.optionsLabel;
      const hasOptions = !!this.product.querySelector('variant-selects');
      this.options.hidden = !hasOptions;
      if (this.options.textContent !== options) this.options.textContent = options;
      const quantity = this.form.elements.namedItem('quantity')?.value || '1';
      this.quantity.hidden = Number(quantity) <= 1;
      const quantityText = `${this.dataset.quantityLabel} : ${quantity}`;
      if (this.quantity.textContent !== quantityText) this.quantity.textContent = quantityText;

      const error = this.product.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (this.submitted && error) {
        this.submitted = false;
        error.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      if (!busy) this.submitted = false;
      const rect = this.native.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const barHeight = this.measuredHeight || 82;
      const header = document.querySelector('.shopify-section-header-sticky');
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const nativeVisible = rect.bottom > headerBottom + 6 && rect.top < viewportHeight - barHeight - 6;
      this.setVisible(!!this.native.getClientRects().length && !nativeVisible && !error && !this.hasOverlay() && !this.hasKeyboard());
    }

    selectedVariant() {
      try {
        return JSON.parse(this.product.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
      } catch {
        return null;
      }
    }

    setVisible(visible) {
      this.hidden = !visible;
      document.documentElement.classList.toggle('lbb-mobile-purchase-visible', visible);
      if (visible) {
        this.measuredHeight = Math.ceil(this.getBoundingClientRect().height) || this.measuredHeight || 82;
        const height = `${this.measuredHeight}px`;
        if (document.documentElement.style.getPropertyValue('--lbb-mobile-purchase-height') !== height) {
          document.documentElement.style.setProperty('--lbb-mobile-purchase-height', height);
        }
      }
    }

    submitNative() {
      if (!this.resolveProduct() || this.native.disabled || this.native.getAttribute('aria-disabled') === 'true' || this.native.classList.contains('loading')) return;
      const recipient = this.product.querySelector('recipient-form');
      if (recipient || !this.form.reportValidity()) {
        this.showOptions(recipient || this.form.querySelector(':invalid'));
        return;
      }
      this.submit.disabled = true;
      this.submitted = true;
      this.native.click();
      this.sync();
    }

    showOptions(destination) {
      const target = destination || this.product.querySelector('variant-selects') || this.native;
      this.setVisible(false);
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const focusTarget = target.querySelector?.('input:checked, select, input:not([type="hidden"]), button') || target;
      focusTarget.focus({ preventScroll: true });
    }
  }

  customElements.define('lbb-mobile-buy-bar', LbbMobileBuyBar);
})();
